import { btnDemo } from '../components/cta';
import { escalation, traceCard } from '../components/diagrams';
import { faqAccordion } from '../components/faq-accordion';
import { ARROW_ICON, icon } from '../components/icons';
import { eyebrow, mesh } from '../components/primitives';
import { checklist, ctaBand, sigBand } from '../components/sections';
import { securityForm } from '../components/security-form';
import { securityPdfLink, securityPdfUrl } from '../components/security-pdf';
import { FAQ, faqItems } from '../content/faq';
import { routeHref } from '../lib/links';

/* ============================ SECURITY ============================ */
export function pageSecurity(): string {
  return `
  <section class="hero compact" aria-labelledby="h1">
    ${mesh()}
    <div class="wrap rv">
      ${eyebrow('Security &amp; trust')}
      <h1 id="h1">Enterprise-grade by design. <span class="grad">AI you can explain.</span></h1>
      <p class="lede" style="margin-top:24px">Everything your IT and security reviewers need to understand how ReTax360 approaches access, data protection, AI governance and enterprise controls.</p>
      <div class="ctas"><a class="btn btn-primary" href="${routeHref('/security#request')}">Request Security Information ${ARROW_ICON}</a>${securityPdfLink()}${btnDemo('Book an Enterprise Demo', 'btn-ghost')}</div>
    </div>
  </section>

  <section class="s" id="identity" aria-labelledby="id-h">
    <div class="wrap grid2" style="gap:48px;align-items:start">
      <div class="rv">${eyebrow('Identity &amp; access')}<h2 id="id-h" style="margin:16px 0">Access controls built for enterprise teams.</h2><p class="lede">Identity-provider configuration and implementation requirements are reviewed during enterprise onboarding.</p></div>
      <div class="card rv">${checklist([
        ['key', 'Single Sign-On', 'Supported'],
        ['users', 'Role-Based Access Control', 'Available'],
        ['lock', 'Multi-Factor Authentication', 'Available'],
        ['sliders', 'Role and Permission Controls', 'Available'],
      ])}</div>
    </div>
  </section>

  <section class="s alt" id="data-protection" aria-labelledby="dp-h">
    <div class="wrap grid2" style="gap:48px;align-items:start">
      <div class="rv">${eyebrow('Data ownership &amp; protection')}<h2 id="dp-h" style="margin:16px 0">Your data remains yours.</h2>${FAQ.data[1].split('<p><a')[0]}</div>
      <div class="card rv"><div class="colh">Security controls</div>${checklist([
        ['lock', 'Encryption in transit'],
        ['lock', 'Encryption at rest'],
        ['users', 'Role-based access'],
        ['server', 'Secure cloud infrastructure'],
        ['listChecks', 'Appropriate audit logging'],
      ])}</div>
    </div>
  </section>

  <section class="s" id="ai-governance" aria-labelledby="aig-h">
    <div class="wrap">
      <div class="head rv">${eyebrow('AI governance')}<h2 id="aig-h">Governed AI, not black-box automation.</h2></div>
      <ul class="grid3" data-stagger>${(
        [
          ['userCheck', 'Human approval gates', 'Critical AI-assisted outputs can require designated human review.'],
          [
            'split',
            'Routing governance',
            'Tasks can move between AI, rules, systems and professionals according to workflow and confidence requirements.',
          ],
          [
            'listChecks',
            'Auditability',
            'Workflow actions can be logged to provide visibility into how work moved through the system.',
          ],
          [
            'eye',
            'Model transparency',
            'Enterprise security reviews can include information about the AI providers and systems used for the relevant deployment.',
          ],
          [
            'shield',
            'Data protection',
            'Identifiable customer information is protected according to the applicable enterprise agreement.',
          ],
          [
            'database',
            'De-identified improvement',
            'Where contractually permitted, anonymized, aggregated and de-identified information may be used to improve platform intelligence without attribution to the customer, its clients, properties or transactions.',
          ],
        ] as const
      )
        .map(([i, h, p]) => `<li class="card rv"><span class="ic-tile">${icon(i)}</span><h3>${h}</h3><p>${p}</p></li>`)
        .join('')}</ul>
    </div>
  </section>

  ${sigBand("Trust in AI isn't declared. It's designed: approval gates, visible workflows and a professional who makes the call.", { dark: true, grad: "It's designed:" })}

  <section class="s" id="observability" aria-labelledby="sobs-h">
    <div class="wrap">
      <div class="grid2 obs" style="gap:56px">
        <div class="rv">${eyebrow('Observability')}<h2 id="sobs-h" style="margin:16px 0">Intelligence you can inspect.</h2><p class="lede">Enterprise AI should not operate as an unexplained black box. ReTax360 is designed so organizations can understand how work moved through the workflow.</p></div>
        <div class="rv">${traceCard()}</div>
      </div>
      <div style="margin-top:32px">${escalation({ compact: true })}</div>
    </div>
  </section>

  <section class="s alt" id="compliance" aria-labelledby="comp-h">
    <div class="wrap grid2" style="gap:48px;align-items:start">
      <div class="rv">${eyebrow('Compliance &amp; assurance')}<h2 id="comp-h" style="margin:16px 0">Compliance &amp; Assurance</h2><p class="lede">ReTax360 is designed around enterprise security, privacy and governance requirements. Certification and assessment status is shared accurately during enterprise evaluations.</p></div>
      <div class="card rv"><div class="colh">Enterprise evaluations may include</div>${checklist([
        ['file', 'Security documentation', 'On request'],
        ['fileCheck', 'Data Processing Agreement', 'On request'],
        ['users', 'Subprocessor information', 'On request'],
        ['listChecks', 'Security questionnaires', 'On request'],
        ['layers', 'Architecture review', 'On request'],
        ['eye', 'AI governance review', 'On request'],
      ])}</div>
    </div>
  </section>

  <section class="s" id="faq" aria-labelledby="secfaq-h">
    <div class="wrap narrow">
      <div class="head rv">${eyebrow('Security FAQ')}<h2 id="secfaq-h">Security questions, answered.</h2></div>
      <div class="rv">${faqAccordion(faqItems(['data', 'training', 'sso']), 'secfaq')}</div>
    </div>
  </section>

  <section class="s alt" id="request" aria-labelledby="req-h">
    <div class="wrap demo-grid">
      <div class="rv">${eyebrow('Security review')}<h2 id="req-h" style="margin:16px 0">Request Security Information.</h2><p class="lede">Tell us what your review needs, and our team will follow up.</p>${securityPdfUrl() ? `<p style="margin-top:18px">${securityPdfLink('tlink')}</p><p class="fine">A one-page summary of access controls, data protection, AI governance and compliance information.</p>` : ''}</div>
      <div class="form-card rv" style="--d:80ms">${securityForm()}</div>
    </div>
  </section>
  ${ctaBand()}`;
}
